import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { useModalVideoPreviewStyles } from './modalVideoPreview.styles';
import { useTheme } from '@doxyme/ui-foundation/theme';
import {
  type AvatarProperties,
  Avatar,
} from '@doxyme/ui-foundation/components/avatar';
import { BodyS } from '@doxyme/ui-foundation/typography';
import { Icon } from '@doxyme/icons';
import { useIntl } from '@doxyme/locale';
import { VideoPreview } from '../callSettings/videoPreview';

import type { JSX } from 'react';

type CommonProps = {
  type: 'virtualBackground' | 'settings';
  showProgressBar?: boolean;
  hasVideo?: boolean;
  hasVideoAccess?: boolean;
  videoElement?: HTMLVideoElement | HTMLElement;
};

type Props =
  | ({
      type: 'virtualBackground';
      avatarProps?: never;
    } & CommonProps)
  | ({
      type: 'settings';
      avatarProps: Omit<AvatarProperties, 'size'>;
    } & CommonProps);

export const ModalVideoPreview = ({
  type,
  showProgressBar = false,
  videoElement,
  hasVideo = false,
  hasVideoAccess = false,
  avatarProps,
}: Props): JSX.Element => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const styles = useModalVideoPreviewStyles();

  const defaultNoVideo = (
    <Box css={styles.noVideoContainer}>
      <Box css={styles.noVideoInnerContainer}>
        <Icon.VideoDisabled
          role='presentation'
          size={40}
          color={theme.palette.alert.negative[100]}
          aria-label={formatMessage({
            id: 'virtualBackground.cameraDisabled',
          })}
        />

        {!hasVideoAccess ? (
          <BodyS fontWeight='medium' color={theme.text.generic.badge}>
            {formatMessage({
              id: 'virtualBackground.disableAccessCamera.text',
            })}
          </BodyS>
        ) : (
          <BodyS
            fontWeight='medium'
            color={theme.text.generic.badge}
            css={{
              textAlign: 'center',
            }}
          >
            {formatMessage({
              id: 'virtualBackground.disableCamera.text',
            })}
            <br />
            {formatMessage({
              id: 'virtualBackground.disableCamera.selectingBackgroundText',
            })}
          </BodyS>
        )}
      </Box>
    </Box>
  );

  const avatarNoVideo = (
    <Box css={styles.noVideoContainer}>
      <Avatar
        size='2xl'
        {...avatarProps}
        statusBadgeIconName='VideoDisabled'
        badgeColor='negative'
      />
    </Box>
  );

  return (
    <Box css={styles.container}>
      {hasVideo && (
        <Box css={styles.videoContainer}>
          <Box css={styles.videoInnerContainer}>
            <VideoPreview videoEl={videoElement} />

            {showProgressBar && (
              <Box css={styles.progress}>
                <LinearProgress
                  data-testid='linear-progress'
                  color='primary'
                  variant='indeterminate'
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {!hasVideo
        ? type === 'settings'
          ? avatarNoVideo
          : defaultNoVideo
        : null}
    </Box>
  );
};
